/**
 * Initialize fetch interceptor.
 *
 * @since 1.0.0
 */

import { __ } from '@wordpress/i18n';

import { storeInterceptedData } from '../get-intercepted-data';
import type {
  CartAddItemRequest,
  CartAddItemResponse,
  InterceptedData,
  InterceptorCallback,
} from '../types';

/**
 * Store original fetch function.
 *
 * @since 1.0.0
 */
const originalFetch = window.fetch;

/**
 * Active callback functions.
 *
 * @since 1.0.0
 */
const callbacks: Set<InterceptorCallback> = new Set();

/**
 * Check if URL matches cart add-item endpoint.
 *
 * @since 1.0.0
 *
 * @param url - The URL to check.
 * @return True if URL matches cart endpoints.
 */
const isCartAddItemEndpoint = (url: string): boolean => {
  return url.endsWith('wc/store/v1/cart/add-item') || url.endsWith('wc/store/v1/cart/update-item');
};

/**
 * Extract request data from fetch options.
 *
 * @since 1.0.0
 *
 * @param options - Fetch request options.
 * @return Promise resolving to request data or null.
 */
const extractRequestData = async (options?: RequestInit): Promise<CartAddItemRequest | null> => {
  try {
    if (options?.body) {
      if (typeof options.body === 'string') {
        return JSON.parse(options.body) as CartAddItemRequest;
      } else if (options.body instanceof FormData) {
        const data: Record<string, unknown> = {};
        options.body.forEach((value, key) => {
          data[key] = value;
        });
        return data as CartAddItemRequest;
      }
    }
  } catch {
    // Do nothing
  }
  return null;
};

/**
 * Extract response data.
 *
 * @since 1.0.0
 *
 * @param response - Fetch response object.
 * @return Promise resolving to response data or null.
 */
const extractResponseData = async (response: Response): Promise<CartAddItemResponse | null> => {
  try {
    const clonedResponse = response.clone();
    const data = await clonedResponse.json();
    return data as CartAddItemResponse;
  } catch {
    return null;
  }
};

/**
 * Notify all registered callbacks.
 *
 * @since 1.0.0
 *
 * @param data - Intercepted data to pass to callbacks.
 */
const notifyCallbacks = (data: InterceptedData): void => {
  callbacks.forEach((callback) => {
    try {
      callback(data);
    } catch {
      // Do nothing
    }
  });
};

/**
 * Intercepted fetch function.
 *
 * @since 1.0.0
 *
 * @param input - Fetch input parameter.
 * @param init - Fetch initialization options.
 * @return Promise resolving to Response.
 */
const interceptedFetch: typeof fetch = async (input, init) => {
  let url: string;
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof Request) {
    url = input.url;
  } else {
    url = input.toString();
  }

  // Only intercept cart add-item requests
  if (!isCartAddItemEndpoint(url)) {
    return originalFetch(input, init);
  }

  // Extract request data
  const requestData = await extractRequestData(init);

  try {
    // Call original fetch
    const response = await originalFetch(input, init);

    // Extract response data
    const responseData = await extractResponseData(response);

    // Create intercepted data
    const interceptedData: InterceptedData = {
      request: requestData,
      response: responseData,
      url,
      timestamp: Date.now(),
    };

    // Store data for debugging
    storeInterceptedData(interceptedData);

    // Notify callbacks
    notifyCallbacks(interceptedData);

    return response;
  } catch (error) {
    // Still notify callbacks on error
    const interceptedData: InterceptedData = {
      request: requestData,
      response: null,
      url,
      timestamp: Date.now(),
    };

    // Store data for debugging even on error
    storeInterceptedData(interceptedData);

    notifyCallbacks(interceptedData);

    // Re-throw the error
    throw error;
  }
};

/**
 * Initialize fetch interceptor.
 *
 * @since 1.0.0
 */
export const initFetchInterceptor = (): void => {
  if (window.fetch !== interceptedFetch) {
    window.fetch = interceptedFetch;
  }
};

/**
 * Get callbacks set for internal use.
 *
 * @since 1.0.0
 *
 * @return Set of active callbacks.
 */
export const getCallbacks = (): Set<InterceptorCallback> => {
  return callbacks;
};

/**
 * Get original fetch function for internal use.
 *
 * @since 1.0.0
 *
 * @return Original fetch function.
 */
export const getOriginalFetch = (): typeof fetch => {
  return originalFetch;
};

/**
 * Get intercepted fetch function for internal use.
 *
 * @since 1.0.0
 *
 * @return Intercepted fetch function.
 */
export const getInterceptedFetch = (): typeof fetch => {
  return interceptedFetch;
};
